import { supabase } from '../lib/supabase';

/**
 * Typed error from gemini-proxy (HTTP failure, unusable completion or stream error event).
 * `code` is one of MODEL_UNAVAILABLE, UPSTREAM_ERROR, BAD_REQUEST, RATE_LIMITED, MAX_TOKENS,
 * SAFETY, RECITATION, BLOCKED, MALFORMED_FUNCTION_CALL, EMPTY_RESPONSE, INVALID_RESPONSE,
 * INVALID_JSON, TOOLS_UNSUPPORTED, ABORTED or HTTP_<status>.
 */
export class AiProxyError extends Error {
  /**
   * @param {string} message - Human-readable message.
   * @param {object} [info] - Error facts.
   * @param {string} [info.code] - Error code.
   * @param {number} [info.status] - HTTP status.
   * @param {string|null} [info.model] - Model that failed.
   * @param {string|null} [info.finishReason] - Gemini finishReason.
   * @param {unknown} [info.details] - Upstream details.
   */
  constructor(message, info = {}) {
    super(message);
    this.name = 'AiProxyError';
    this.code = info.code || 'UPSTREAM_ERROR';
    this.status = info.status ?? null;
    this.model = info.model ?? null;
    this.finishReason = info.finishReason ?? null;
    this.details = info.details ?? null;
  }
}

/**
 * Wrapper for Supabase Edge Function calls.
 *
 * gemini-proxy protocol versions:
 * - v1 (previous deploy): body { prompt, systemInstruction, modelName, accountId,
 *   generationConfig, parts, stream }. Non-stream reply { text }. Stream NDJSON lines
 *   { delta } / { thoughtDelta } / { error }. A body without prompt -> HTTP 400 "Prompt is required.".
 * - v2: adds { probe, task, contents, tools, toolConfig, profile, seed }. Non-stream reply
 *   { proxyVersion: 2, model, task, fellBack, text, functionCalls, finishReason, usage,
 *   modelContent, thought? }. Stream NDJSON lines (v1 keys kept alongside so old parsers work):
 *     { type:'meta', model, proxyVersion, task, fellBack }
 *     { type:'thought', text, thoughtDelta }
 *     { type:'text', text, delta }
 *     { type:'functionCall', name, args, id?, thoughtSignature? }
 *     { type:'done', finishReason, usage, model, modelContent }
 *     { type:'error', message, code, error? }   (`error` only for upstream failures)
 *   `modelContent` is the model turn (thought text removed, thought signatures kept); append
 *   it verbatim to `contents` before sending functionResponse parts on the next turn.
 * Use {@link EdgeFunctionService#getProxyCapabilities} to pick the protocol.
 */
class EdgeFunctionService {
  constructor() {
    this.baseUrl = process.env.REACT_APP_SUPABASE_URL || '';
    /** @type {Promise<{proxyVersion: number, features: string[], models?: object|null}> | null} */
    this.proxyCapabilitiesPromise = null;
  }

  /**
   * Call a Supabase Edge Function
   * @param {string} functionName - Name of the edge function
   * @param {object} payload - Data to send to the function
   * @returns {Promise<object>} Response from the edge function
   */
  async callFunction(functionName, payload) {
    try {
      // Get the current session token
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        throw new Error('Not authenticated');
      }

      const { data, error } = await supabase.functions.invoke(functionName, {
        body: payload,
      });

      if (error) {
        console.error(`Edge function ${functionName} error:`, {
          name: error.name,
          message: error.message,
          context: error.context,
        });
        const serverMessage =
          data && typeof data === "object" && typeof data.error === "string"
            ? data.error
            : null;
        if (serverMessage) {
          throw new Error(serverMessage);
        }
        throw error;
      }

      return data;
    } catch (error) {
      console.error(`Error calling edge function ${functionName}:`, error);
      throw error;
    }
  }

  /**
   * Generate a document asynchronously using edge function
   * @param {object} params - Document generation parameters
   * @param {string[]} [params.requirementIds] - Optional subset of requirements to use as evidence.
   * @param {string[]} [params.sessionIds] - Optional subset of sessions to use as evidence.
   * @param {number} [params.variant] - Regenerate counter (changes the generation seed).
   * @returns {Promise<object>} Request details including requestId
   */
  async generateDocument(params) {
    const {
      requestId,
      projectId,
      templateId,
      documentName,
      tone,
      contentSource,
      existingDocumentId,
      requirementIds,
      sessionIds,
      variant,
    } = params;

    return await this.callFunction('generate-document', {
      requestId,
      projectId,
      templateId,
      documentName,
      tone,
      contentSource,
      existingDocumentId,
      ...(Array.isArray(requirementIds) ? { requirementIds } : {}),
      ...(Array.isArray(sessionIds) ? { sessionIds } : {}),
      ...(Number.isInteger(variant) ? { variant } : {}),
    });
  }

  /**
   * Extract slide JSON + theme from a reference .pptx via Edge → Cloud Run (includes optional Gemini refinement).
   *
   * @deprecated Use {@link importPresentationTemplate} for the async server-side pipeline.
   *   This synchronous path is kept for internal tooling only.
   * @param {object} params - Upload parameters.
   * @param {string} params.fileBase64 - Base64-encoded .pptx bytes.
   * @param {string} [params.fileName] - Original file name for metadata.
   * @param {string} [params.accountId] - Active account id (the server picks the model from its tier).
   * @param {boolean} [params.skipExtractAi] - If true, deterministic parse only (worker testing).
   * @returns {Promise<{ success: boolean, template: object }>} Parsed template object for the rich editor.
   */
  async extractPresentationTemplate(params) {
    const { fileBase64, fileName, accountId, skipExtractAi } = params;
    return await this.callFunction('extract-presentation-template', {
      fileBase64,
      fileName,
      ...(accountId ? { accountId } : {}),
      ...(typeof skipExtractAi === 'boolean' ? { skipExtractAi } : {}),
    });
  }

  /**
   * Queue an async PowerPoint template import on Cloud Run.
   *
   * The caller must already have created an `ai_requests` row (type=`template_import`) via
   * {@link AIRequestContext#createRequest}; the worker updates that row with progress and,
   * on success, inserts a draft template into the `templates` table. The completed row's
   * `result_data.templateId` identifies the new template for navigation.
   *
   * @param {object} params - Import parameters.
   * @param {string} params.requestId - Id of the pre-created ai_requests row.
   * @param {string} params.fileBase64 - Base64-encoded .pptx bytes (no data-URL prefix).
   * @param {string} [params.fileName] - Original file name (used as template name fallback).
   * @param {string} [params.accountId] - Active account id (required for RLS on the templates insert).
   * @param {string} [params.description] - Optional user-supplied description for the draft template.
   * @returns {Promise<{ success: boolean, requestId: string, status: string }>}
   * @example
   *   const req = await createRequest({ type: 'template_import', title: `Importing ${file.name}` });
   *   await edgeFunctionService.importPresentationTemplate({
   *     requestId: req.id, fileBase64, fileName: file.name, accountId,
   *   });
   */
  async importPresentationTemplate(params) {
    const { requestId, fileBase64, fileName, accountId, description } = params || {};
    if (!requestId) throw new Error('requestId is required');
    if (!fileBase64) throw new Error('fileBase64 is required');
    return await this.callFunction('import-presentation-template', {
      requestId,
      fileBase64,
      fileName,
      ...(accountId ? { accountId } : {}),
      ...(description ? { description } : {}),
    });
  }

  /**
   * Queue project context ingestion for a previously uploaded Storage object.
   * @param {object} params - Ingestion parameters.
   * @param {string} params.requestId - Existing ai_requests row id.
   * @param {string} params.fileId - project_context_files row id.
   * @returns {Promise<{success: boolean, requestId: string, status: string}>} Queue result.
   */
  async ingestProjectContext(params) {
    const { requestId, fileId } = params || {};
    if (!requestId) throw new Error('requestId is required');
    if (!fileId) throw new Error('fileId is required');
    return await this.callFunction('ingest-project-context', {
      requestId,
      fileId,
    });
  }

  /**
   * Retrieve token-bounded project context snippets for a query.
   * @param {object} params - Retrieval parameters.
   * @param {string} params.projectId - Project id.
   * @param {string} params.query - User query or generation intent.
   * @param {number} [params.matchCount] - Maximum chunk count.
   * @param {number} [params.maxChars] - Maximum returned characters.
   * @param {boolean} [params.includeImages] - Whether to include signed image URLs for explicit visual questions.
   * @returns {Promise<{results: Array, images?: Array}>} Retrieved context.
   */
  async retrieveProjectContext(params) {
    const { projectId, query, matchCount, maxChars, includeImages } = params || {};
    if (!projectId) throw new Error('projectId is required');
    if (!query) throw new Error('query is required');
    return await this.callFunction('retrieve-project-context', {
      projectId,
      query,
      ...(matchCount ? { matchCount } : {}),
      ...(maxChars ? { maxChars } : {}),
      ...(includeImages ? { includeImages } : {}),
    });
  }

  /**
   * Call the Gemini proxy Edge Function with the v1-compatible request shape (works with v1
   * and v2 proxies). Prefer `aiService.runTask` for new code.
   * @param {object} params - Gemini request parameters.
   * @param {string} params.prompt - User prompt text.
   * @param {string} params.systemInstruction - System instruction text.
   * @param {string} [params.modelName] - Legacy model id (v1 routing; v2 maps it to a task class).
   * @param {string} [params.task] - Task id (v2 routing; ignored by v1).
   * @param {string} [params.accountId] - Active account id (for plan → model tier on server).
   * @param {object} params.generationConfig - Gemini generation settings.
   * @param {object[]} [params.safetySettings] - Gemini safety settings.
   * @param {Array<object>} [params.parts] - Optional Gemini content parts for explicit multimodal requests.
   * @returns {Promise<{text: string, model?: string, proxyVersion?: number}>} Gemini response.
   */
  async callGemini(params) {
    return await this.callFunction('gemini-proxy', params);
  }

  /**
   * Detect the deployed gemini-proxy protocol once per session (cached promise).
   * A v1 proxy answers the probe with HTTP 400 "Prompt is required." -> `{ proxyVersion: 1 }`.
   * Inconclusive failures (offline, auth not ready) resolve to v1 for that call but are not cached.
   * @returns {Promise<{proxyVersion: number, features: string[], models?: object|null}>} Capabilities.
   */
  getProxyCapabilities() {
    if (!this.proxyCapabilitiesPromise) {
      const probe = this.probeProxy().catch((error) => {
        console.warn('gemini-proxy probe inconclusive; using v1 protocol for now:', error?.message || error);
        if (this.proxyCapabilitiesPromise === probe) {
          this.proxyCapabilitiesPromise = null;
        }
        return { proxyVersion: 1, features: [], models: null };
      });
      this.proxyCapabilitiesPromise = probe;
    }
    return this.proxyCapabilitiesPromise;
  }

  /**
   * Forget cached proxy capabilities (e.g. in tests).
   * @returns {void}
   */
  resetProxyCapabilities() {
    this.proxyCapabilitiesPromise = null;
  }

  /**
   * POST `{ probe: true }` to gemini-proxy.
   * @returns {Promise<{proxyVersion: number, features: string[], models?: object|null}>} Capabilities.
   * @throws {Error} When the answer is inconclusive (network/auth/server error).
   */
  async probeProxy() {
    const res = await this.postGeminiProxy({ probe: true });
    let body = null;
    try {
      body = await res.json();
    } catch {
      body = null;
    }
    if (res.ok && body && Number(body.proxyVersion) >= 2) {
      return {
        proxyVersion: Number(body.proxyVersion),
        features: Array.isArray(body.features) ? body.features : [],
        models: body.models || null,
      };
    }
    const message = typeof body?.error === 'string' ? body.error : '';
    if (/prompt is required/i.test(message)) {
      return { proxyVersion: 1, features: [], models: null };
    }
    throw new Error(message || `Probe failed (HTTP ${res.status})`);
  }

  /**
   * Authenticated POST to gemini-proxy.
   * @param {object} body - Request body.
   * @param {AbortSignal} [signal] - Abort signal.
   * @returns {Promise<Response>} Raw fetch response.
   */
  async postGeminiProxy(body, signal) {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new AiProxyError('Not authenticated', { code: 'HTTP_401', status: 401 });
    }
    return fetch(`${this.baseUrl}/functions/v1/gemini-proxy`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
        apikey: process.env.REACT_APP_SUPABASE_ANON_KEY || '',
      },
      body: JSON.stringify(body),
      signal,
    });
  }

  /**
   * Authenticated POST of one Inky turn to the inky-agent edge function (Inky's server-owned
   * agent loop, CONTRACTS-W2 §C). Returns the raw response so the caller can branch on the
   * status before reading the body:
   *  - 200 `text/event-stream`: `data: {json}` frames (turn_start, text_delta, text_reset,
   *    tool_start, tool_end, client_action, done, error) plus `: keep-alive` comments; read it
   *    with src/assistant/inkyStream.js readInkyStream.
   *  - 400 / 401 / 404 / 409 JSON `{ error, code, conversationId? }` before anything was written
   *    (409 TURN_BUSY: another turn holds this conversation).
   *  - 404 without our JSON / network failure: the function is not deployed (use the browser loop).
   * A dropped connection does NOT stop the turn on the server (it persists its reply; poll the
   * thread). Stop is explicit: {@link EdgeFunctionService#stopInkyAgent}.
   * @param {object} body - Turn request ({ conversationId?, message, existingUserMessageId?,
   *   pageContext, accountId, clientTools, contextKey?, newConversationTitle?, variant?, turnKey? }).
   * @param {AbortSignal} [signal] - Ends the request in this browser only.
   * @returns {Promise<Response>} Raw fetch response.
   */
  async postInkyAgent(body, signal) {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new AiProxyError('Not authenticated', { code: 'HTTP_401', status: 401 });
    }
    return fetch(`${this.baseUrl}/functions/v1/inky-agent`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        Authorization: `Bearer ${session.access_token}`,
        apikey: process.env.REACT_APP_SUPABASE_ANON_KEY || '',
      },
      body: JSON.stringify(body),
      signal,
    });
  }

  /**
   * Ask a running inky-agent turn to stop (Stop button). The server ends the round in flight
   * and saves what already ran as the reply.
   * @param {{turnId: string, conversationId?: string|null, discard?: boolean}} params - The turn
   *   (its `turnKey`), its conversation when known, and `discard` when this browser never saw
   *   the turn start (the server then leaves no trace of it if nothing ran).
   * @returns {Promise<{ok: boolean, found?: boolean, conversationId?: string|null}>} Outcome;
   *   `ok: false` when the server could not record the stop (or predates explicit Stop).
   */
  async stopInkyAgent({ turnId, conversationId = null, discard = false }) {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return { ok: false };
    try {
      const res = await fetch(`${this.baseUrl}/functions/v1/inky-agent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
          apikey: process.env.REACT_APP_SUPABASE_ANON_KEY || '',
        },
        body: JSON.stringify({ stop: true, turnId, conversationId: conversationId || undefined, discard }),
      });
      const data = await res.json().catch(() => ({}));
      return res.ok && data?.ok ? { ok: true, found: Boolean(data.found), conversationId: data.conversationId || null } : { ok: false };
    } catch (error) {
      console.warn('[inky] stop request failed', error);
      return { ok: false };
    }
  }

  /**
   * Build an AiProxyError from a failed proxy response.
   * @param {Response} res - Failed response.
   * @returns {Promise<AiProxyError>} Typed error.
   */
  async errorFromResponse(res) {
    let body = null;
    try {
      body = await res.json();
    } catch {
      body = null;
    }
    const message = typeof body?.error === 'string' ? body.error : `AI request failed (HTTP ${res.status})`;
    const finishReason = body?.finishReason ?? body?.details?.finishReason ?? null;
    return new AiProxyError(message, {
      code: body?.code || this.deriveErrorCode(res.status, finishReason, body?.details?.blockReason),
      status: res.status,
      model: body?.model ?? null,
      finishReason,
      details: body?.details ?? null,
    });
  }

  /**
   * Typed error code for a reply without `code` (v1 proxy), from finishReason / blockReason / status,
   * so callers can branch on MAX_TOKENS, SAFETY, ... against either proxy version.
   * @param {number} status - HTTP status.
   * @param {string|null} finishReason - Gemini finishReason from details.
   * @param {string|null} [blockReason] - Gemini promptFeedback.blockReason from details.
   * @returns {string} Error code.
   */
  deriveErrorCode(status, finishReason, blockReason) {
    const reason = String(finishReason || '').toUpperCase();
    if (reason === 'MAX_TOKENS') return 'MAX_TOKENS';
    if (reason === 'SAFETY') return 'SAFETY';
    if (reason === 'RECITATION') return 'RECITATION';
    if (reason === 'MALFORMED_FUNCTION_CALL') return 'MALFORMED_FUNCTION_CALL';
    if (blockReason || ['BLOCKLIST', 'PROHIBITED_CONTENT', 'SPII'].includes(reason)) return 'BLOCKED';
    if (status === 429) return 'RATE_LIMITED';
    if (status === 422) return 'EMPTY_RESPONSE';
    return `HTTP_${status}`;
  }

  /**
   * Non-streaming gemini-proxy call with abort support and typed errors.
   * @param {object} body - v1 or v2 request body.
   * @param {object} [options] - Options.
   * @param {AbortSignal} [options.signal] - Abort signal.
   * @returns {Promise<object>} Parsed JSON response (`text` present on success).
   * @throws {AiProxyError} On failure.
   */
  async callGeminiProxy(body, { signal } = {}) {
    let res;
    try {
      res = await this.postGeminiProxy(body, signal);
    } catch (error) {
      if (error?.name === 'AbortError') throw new AiProxyError('Request cancelled.', { code: 'ABORTED' });
      throw error;
    }
    if (!res.ok) {
      throw await this.errorFromResponse(res);
    }
    try {
      return await res.json();
    } catch (error) {
      if (error?.name === 'AbortError') throw new AiProxyError('Request cancelled.', { code: 'ABORTED' });
      throw new AiProxyError('Invalid response from AI service.', { code: 'INVALID_RESPONSE', status: res.status });
    }
  }

  /**
   * Streaming gemini-proxy call. Normalizes v2 events and v1 lines into v2-shaped events.
   * @param {object} body - Request body (stream is forced on).
   * @param {object} [options] - Options.
   * @param {AbortSignal} [options.signal] - Abort signal.
   * @param {function(object): void} [options.onEvent] - Receives { type: 'meta'|'thought'|'text'|'functionCall'|'done'|'error', ... }.
   * @returns {Promise<{text: string, thought: string, functionCalls: Array<object>, model: string|null, finishReason: string|null, usage: object|null, modelContent: object|null}>} Aggregate result.
   * @throws {AiProxyError} On HTTP failure or an `error` event.
   */
  async streamGeminiProxy(body, { signal, onEvent } = {}) {
    let res;
    try {
      res = await this.postGeminiProxy({ ...body, stream: true }, signal);
    } catch (error) {
      if (error?.name === 'AbortError') throw new AiProxyError('Request cancelled.', { code: 'ABORTED' });
      throw error;
    }
    if (!res.ok) {
      throw await this.errorFromResponse(res);
    }
    if (!res.body) {
      throw new AiProxyError('No response body', { code: 'INVALID_RESPONSE' });
    }

    const result = {
      text: '',
      thought: '',
      functionCalls: [],
      model: res.headers?.get?.('X-Quill-Model') || null,
      finishReason: null,
      usage: null,
      modelContent: null,
    };
    const emit = (event) => {
      if (typeof onEvent === 'function') onEvent(event);
    };

    /**
     * Handle one parsed NDJSON object (v2 typed event or v1 key-only line).
     * @param {object} obj - Parsed line.
     * @returns {void}
     */
    const handle = (obj) => {
      let type = obj.type;
      if (!type) {
        if (typeof obj.delta === 'string') type = 'text';
        else if (typeof obj.thoughtDelta === 'string') type = 'thought';
        else if (obj.error) type = 'error';
      }
      if (type === 'meta') {
        result.model = obj.model || result.model;
        emit(obj);
      } else if (type === 'text') {
        const text = typeof obj.text === 'string' ? obj.text : obj.delta;
        if (typeof text === 'string' && text) {
          result.text += text;
          emit({ type: 'text', text });
        }
      } else if (type === 'thought') {
        const text = typeof obj.text === 'string' ? obj.text : obj.thoughtDelta;
        if (typeof text === 'string' && text) {
          result.thought += text;
          emit({ type: 'thought', text });
        }
      } else if (type === 'functionCall') {
        const call = {
          name: obj.name,
          args: obj.args && typeof obj.args === 'object' ? obj.args : {},
          ...(obj.id ? { id: obj.id } : {}),
          ...(obj.thoughtSignature ? { thoughtSignature: obj.thoughtSignature } : {}),
        };
        result.functionCalls.push(call);
        emit({ type: 'functionCall', ...call });
      } else if (type === 'done') {
        result.finishReason = obj.finishReason ?? null;
        result.usage = obj.usage ?? null;
        result.model = obj.model || result.model;
        result.modelContent = obj.modelContent ?? null;
        emit(obj);
      } else if (type === 'error') {
        const message = obj.message || (typeof obj.error === 'string' ? obj.error : 'Stream error');
        const code = obj.code || 'UPSTREAM_ERROR';
        emit({ type: 'error', message, code });
        throw new AiProxyError(message, {
          code,
          model: result.model,
          finishReason: obj.finishReason ?? null,
          details: { partialText: result.text },
        });
      }
    };

    /**
     * Parse and handle one line.
     * @param {string} line - Raw line.
     * @returns {void}
     */
    const processLine = (line) => {
      const t = line.trim();
      if (!t) return;
      let obj;
      try {
        obj = JSON.parse(t);
      } catch {
        return;
      }
      if (obj && typeof obj === 'object') handle(obj);
    };

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        lines.forEach(processLine);
      }
      buffer += decoder.decode();
      if (buffer) processLine(buffer);
    } catch (error) {
      if (error?.name === 'AbortError') throw new AiProxyError('Request cancelled.', { code: 'ABORTED' });
      throw error;
    }
    return result;
  }

  /**
   * Stream Gemini output via gemini-proxy, v1-compatible: reads the `delta` / `thoughtDelta`
   * keys that both proxy versions emit. Prefer `aiService.streamTask` for new code.
   * @param {object} params - Same as callGemini, plus stream is set server-side.
   * @param {object} [options] - Stream options.
   * @param {function(string): void} [options.onTextDelta] - Called for each answer text delta (non-thought parts).
   * @param {function(string): void} [options.onThoughtDelta] - Called for each thought-summary delta when enabled.
   * @param {AbortSignal} [options.signal] - Abort fetch.
   * @returns {Promise<string>} Full concatenated answer text (for JSON parsing; excludes thoughts).
   */
  async callGeminiStream(params, options = {}) {
    const { onTextDelta, onThoughtDelta, signal } = options;
    const { data: { session } } = await supabase.auth.getSession();

    if (!session) {
      throw new Error('Not authenticated');
    }

    const url = `${this.baseUrl}/functions/v1/gemini-proxy`;
    const anonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
        apikey: anonKey,
      },
      body: JSON.stringify({ ...params, stream: true }),
      signal,
    });

    if (!res.ok) {
      let errMsg = `HTTP ${res.status}`;
      try {
        const errBody = await res.json();
        if (errBody && typeof errBody.error === 'string') {
          errMsg = errBody.error;
        }
      } catch {
        /* ignore */
      }
      throw new Error(errMsg);
    }

    if (!res.body) {
      throw new Error('No response body');
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let fullText = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        const t = line.trim();
        if (!t) continue;
        try {
          const obj = JSON.parse(t);
          if (obj.error) {
            throw new Error(typeof obj.error === 'string' ? obj.error : 'Stream error');
          }
          if (obj.thoughtDelta && typeof obj.thoughtDelta === 'string') {
            if (typeof onThoughtDelta === 'function') {
              onThoughtDelta(obj.thoughtDelta);
            }
          }
          if (obj.delta && typeof obj.delta === 'string') {
            fullText += obj.delta;
            if (typeof onTextDelta === 'function') {
              onTextDelta(obj.delta);
            }
          }
        } catch (e) {
          if (e instanceof SyntaxError) {
            continue;
          }
          throw e;
        }
      }
    }

    return fullText;
  }
}

const edgeFunctionService = new EdgeFunctionService();
export default edgeFunctionService;
